import React, { useState, useEffect, useRef } from "react";
import { NavLink } from "react-router-dom";
import { useTexts } from "../hooks/useTexts";

const ToolsDropdown: React.FC = () => {
    const { getText } = useTexts();
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const tools = [
        { to: "/chatbot", icon: "🤖", title: getText("tools_chatbot", "AI Chatbot"), desc: getText("tools_chatbot_desc", "Fragen stellen & Antworten erhalten") },
        { to: "/tasks", icon: "📝", title: getText("tools_tasks", "Aufgaben Generator"), desc: getText("tools_tasks_desc", "Automatisierte Aufgaben generieren") },
        { to: "/youtube-tasks", icon: "🎬", title: getText("tools_youtube", "YouTube Aufgaben"), desc: getText("tools_youtube_desc", "Aufgaben aus YouTube-Videos erstellen") },
        { to: "/kahoot-creator", icon: "🎮", title: getText("tools_kahoot", "Kahoot Generator"), desc: getText("tools_kahoot_desc", "Interaktive Quizze für Kahoot erstellen") },
    ];

    const comingSoon = [
        { icon: "📈", title: getText("tools_quiz", "Quiz Generator"), desc: getText("tools_quiz_desc", "Bald verfügbar") },
        { icon: "📊", title: getText("tools_analytics", "Analytics Dashboard"), desc: getText("tools_analytics_desc", "Bald verfügbar") },
    ];

    return (
        <div className="relative" ref={ref}>
            <button
                className="flex items-center gap-2 px-4 py-2 rounded-xl text-gray-600 hover:text-gray-800 hover:bg-black/5 transition-all duration-200 text-sm font-medium"
                onClick={() => setOpen(!open)}
            >
                <span>{getText("tools_menu_title", "🛠️ Tools")}</span>
                <svg width="16" height="16" viewBox="0 0 24 24" className={`transition-transform ${open ? 'rotate-180' : ''}`}>
                    <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" />
                </svg>
            </button>

            {open && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white border border-gray-200 rounded-2xl p-4 shadow-xl z-50">
                    <div className="mb-3">
                        <div className="font-semibold text-gray-800">{getText("tools_header", "🧰 AI-Tools")}</div>
                        <div className="text-xs text-gray-500">{getText("tools_subheader", "Intelligente Helfer für dein Lernen")}</div>
                    </div>

                    {tools.map((tool) => (
                        <NavLink
                            key={tool.to}
                            to={tool.to}
                            onClick={() => setOpen(false)}
                            className="flex items-start gap-3 p-3 rounded-xl hover:bg-green-500/10 hover:text-gray-800 text-gray-600 transition-all duration-200"
                        >
                            <div className="flex items-center justify-center w-10 h-10 bg-black/5 rounded-lg text-xl">
                                {tool.icon}
                            </div>
                            <div className="flex-1">
                                <div className="font-semibold">{tool.title}</div>
                                <div className="text-xs text-gray-500">{tool.desc}</div>
                            </div>
                        </NavLink>
                    ))}

                    <div className="h-px bg-gray-200 my-3" />
                    <div className="font-semibold text-gray-800 mb-2">{getText("tools_coming_soon", "⚡ In Entwicklung")}</div>

                    {comingSoon.map((item) => (
                        <div key={item.title} className="flex items-start gap-3 p-3 rounded-xl text-gray-400 opacity-60 cursor-not-allowed">
                            <div className="flex items-center justify-center w-10 h-10 bg-black/5 rounded-lg text-xl">
                                {item.icon}
                            </div>
                            <div className="flex-1">
                                <div className="font-semibold">{item.title}</div>
                                <div className="text-xs">{item.desc}</div>
                            </div>
                            <span className="px-2 py-1 text-xs bg-gray-100 text-gray-500 rounded-full">{getText("tools_soon_badge", "Soon")}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ToolsDropdown;